// ABOUTME: The fetch client for the talent's job feed on pro-core: bearer token, Accept-Language, the page in the query
// ABOUTME: and the filters in the body, and the error mapping that keeps "the feed is off" apart from "no such listing";
// ABOUTME: also the job of an application and of my job, read as that same detail with its source.
import { ApiError, ResponseError } from 'utils/api';
import { env } from '../../constants';
import {
  APPLY_API_URL,
  ASSIGNMENT_JOB_API_URL,
  JOB_LISTING_NOT_FOUND_ERROR_CODE,
  JOB_LISTINGS_API_URL,
  JOB_LISTINGS_UNAVAILABLE_ERROR_CODE,
  PAGE_SIZE
} from './constants';
import {
  ApplyAnswer,
  ApplyRequest,
  CommuteAnswer,
  FilterOptions,
  FilterOptionsResponse,
  JobListing,
  JobListingResponse,
  JobListingSearchCriteria,
  JobListingsPage,
  JobPage,
  JobPageResponse,
  ListingEngagementAction,
  ListingEngagementSource,
  SearchPreferences,
  SearchPreferencesResponse,
  TravelMode,
  WeekFitAnswer,
  WeekFitRequest
} from './types';

const baseUrl = () => `${env.VITE_API_BASE_URL}/${JOB_LISTINGS_API_URL}`;

const talentHeaders = (token: string, language: string) => ({
  Authorization: `Bearer ${token}`,
  'Accept-Language': language
});

const jsonHeaders = (token: string, language: string) => ({
  ...talentHeaders(token, language),
  'Content-Type': 'application/json'
});

/** The structured body behind a failed call, or an empty one when there is nothing to read. */
const readError = async (response: Response): Promise<ResponseError> => {
  try {
    const json = (await response.json()) as Partial<ResponseError>;
    return { code: json.code ?? '', values: json.values ?? {} };
  } catch {
    return { code: '', values: {} };
  }
};

/**
 * Every failure of these calls, as an ApiError the screens branch on.
 *
 * A 404 whose body does not name a listing is PRO_JOB_LISTING being off for this talent (pro-core answers
 * `error.not-found`), which the screens show as the friendly off state. A listing that is not there, or not one this
 * talent may see, arrives as a 400 `error.job-listing.not-found` and travels as itself (also kept apart should it ever
 * come as a 404, as the operator routes answer it). A 401 reloads first so the Auth0
 * provider renews the session, as every talent endpoint here does.
 */
const throwJobListingError = async (response: Response): Promise<never> => {
  if (response.status === 401) {
    window.location.reload();
  }

  const error = await readError(response);

  if (
    response.status === 404 &&
    error.code !== JOB_LISTING_NOT_FOUND_ERROR_CODE
  ) {
    throw new ApiError({
      code: JOB_LISTINGS_UNAVAILABLE_ERROR_CODE,
      values: {}
    });
  }

  throw new ApiError(error);
};

/**
 * POST /talent/listings/search: one page of the feed. The filters go in the body, always as an object even when
 * there are none, because an absent body and an empty criteria object are two different requests to pro-core. The
 * page and its size go in the query, which is where Spring's Pageable reads them.
 */
export const searchJobListings = async (
  token: string,
  language: string,
  criteria: JobListingSearchCriteria,
  page: number
): Promise<JobListingsPage> => {
  const response = await fetch(
    `${baseUrl()}/search?page=${page}&size=${PAGE_SIZE}`,
    {
      method: 'POST',
      headers: jsonHeaders(token, language),
      body: JSON.stringify(criteria)
    }
  );

  if (!response.ok) {
    await throwJobListingError(response);
  }

  // pro-core serialises with non_empty, so an empty page arrives without `data`; the screen is owed a list either way.
  const json = (await response.json()) as Partial<JobListingsPage>;
  return { data: json.data ?? [], meta: json.meta! };
};

/** GET /talent/listings/{refId}: the card plus the whole render the operator previewed. */
export const fetchJobListing = async (
  token: string,
  language: string,
  refId: string
): Promise<JobListing> => {
  const response = await fetch(`${baseUrl()}/${refId}`, {
    method: 'GET',
    headers: talentHeaders(token, language)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as JobListingResponse;
  return json.data;
};

/**
 * GET /talent/listings/filterOptions: what the occupation and company filters can pick from. With non_empty an
 * empty list arrives as no list at all, so both are defaulted here and the sheet never has to ask.
 */
export const fetchFilterOptions = async (
  token: string,
  language: string
): Promise<FilterOptions> => {
  const response = await fetch(`${baseUrl()}/filterOptions`, {
    method: 'GET',
    headers: talentHeaders(token, language)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as FilterOptionsResponse;
  return {
    occupations: json.data?.occupations ?? [],
    companies: json.data?.companies ?? []
  };
};

/** A talent who saved nothing reads two empty lists; with non_empty they arrive as no lists at all. */
const withLists = (
  preferences: Partial<SearchPreferences> | undefined
): SearchPreferences => ({
  savedSearches: preferences?.savedSearches ?? [],
  savedPoints: preferences?.savedPoints ?? []
});

/** GET /talent/listings/searchPreferences: the talent's saved searches and points; two empty lists for a first visit. */
export const fetchSearchPreferences = async (
  token: string,
  language: string
): Promise<SearchPreferences> => {
  const response = await fetch(`${baseUrl()}/searchPreferences`, {
    method: 'GET',
    headers: talentHeaders(token, language)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as SearchPreferencesResponse;
  return withLists(json.data);
};

/** PUT /talent/listings/searchPreferences: the document replaced whole; the answer carries the refIds the server minted. */
export const saveSearchPreferences = async (
  token: string,
  language: string,
  preferences: SearchPreferences
): Promise<SearchPreferences> => {
  const response = await fetch(`${baseUrl()}/searchPreferences`, {
    method: 'PUT',
    headers: jsonHeaders(token, language),
    body: JSON.stringify(preferences)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as SearchPreferencesResponse;
  return withLists(json.data);
};

/** The request as sent: an empty `startFrom` (a cleared date field) is left out rather than sent as "". */
const withoutEmptyStart = (request: ApplyRequest): ApplyRequest =>
  request.availability && !request.availability.startFrom
    ? {
        ...request,
        availability: { ...request.availability, startFrom: undefined }
      }
    : request;

/**
 * POST /talent/applications: apply to a listing. The status code is the only place the two outcomes differ, so it is
 * read once, here: 201 is a new application, 200 the one I already had at that opening (a second wording of it), and
 * pro-core writes no second candidacy for the latter. The body is the application's refId.
 *
 * With PJL the week the talent sent travels along (`availability`, `saveToProfile`, `confirmations`). pro-core reads them
 * only while PRO_APPLY_V2 is on for the talent, and only then answers `nextAction` next to `data`; a request without them,
 * or with the flag off, is today's apply and today's `{ data }`. A start the date field left empty is not sent: pro-core
 * reads `startFrom` as a date, and an empty one would refuse the whole application.
 */
export const applyToJobListing = async (
  token: string,
  language: string,
  request: ApplyRequest
): Promise<ApplyAnswer> => {
  const response = await fetch(`${env.VITE_API_BASE_URL}/${APPLY_API_URL}`, {
    method: 'POST',
    headers: jsonHeaders(token, language),
    body: JSON.stringify(withoutEmptyStart(request))
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as {
    data: string;
    nextAction?: ApplyAnswer['nextAction'];
  };
  return {
    applicationRefId: json.data,
    created: response.status === 201,
    nextAction: json.nextAction
  };
};

/**
 * POST /talent/listings/{refId}/weekFit (pro-core #2102): the listing's days against the talent's week. Without days
 * it reads „Meine Verfügbarkeit“; with them, the week as edited in the apply flow, where a weekday not sent has no time.
 * A pure read: nothing is stored. Behind PRO_JOB_LISTING and PRO_APPLY_V2: while either is off it answers 404
 * `error.not-found`, which arrives here as the feed's "unavailable". pro-core writes `days`, `yours` and
 * `yoursPreferred` always, and `weekend` only when the listing says something about Saturdays or holidays; `profile`
 * is filled up here in case a value is missing. The body may carry the hours a week and the start the talent named;
 * `warnings` (WEEKLY_HOURS, START_DATE, decision of 26 Oct 2026) come back only when there is one, and never block.
 */
export const fetchWeekFit = async (
  token: string,
  language: string,
  refId: string,
  request: WeekFitRequest
): Promise<WeekFitAnswer> => {
  const response = await fetch(`${baseUrl()}/${refId}/weekFit`, {
    method: 'POST',
    headers: jsonHeaders(token, language),
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as { data: WeekFitAnswer };
  return {
    ...json.data,
    days: (json.data.days ?? []).map(day => ({
      ...day,
      yours: day.yours ?? [],
      yoursPreferred: day.yoursPreferred ?? []
    })),
    profile: json.data.profile ?? {}
  };
};

/**
 * POST /talent/listings/{refId}/engagements (zenjob/pro-core-service#2103): one step a talent took with a listing (the
 * card seen, the detail opened, a step of the apply flow), counted per listing; 204, nothing to read. `source` is where
 * in the app (`feed`, `detail` or `apply`), at most 64 characters. 404 `error.not-found` while PRO_LISTING_FEED or PRO_JOB_LISTING is off.
 */
export const recordEngagement = async (
  token: string,
  language: string,
  refId: string,
  action: ListingEngagementAction,
  source: ListingEngagementSource
): Promise<void> => {
  const response = await fetch(`${baseUrl()}/${refId}/engagements`, {
    method: 'POST',
    headers: jsonHeaders(token, language),
    body: JSON.stringify({ action, source })
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }
};

/**
 * GET /talent/listings/{refId}/commute: minutes to each place for one mode, from the talent's point. Not served by
 * pro-core yet (PJL-056, zenjob/pro-core-service#2034), so the details offer it only on mock data (see COMMUTE_SERVED).
 */
export const fetchCommute = async (
  token: string,
  language: string,
  refId: string,
  mode: TravelMode
): Promise<CommuteAnswer> => {
  const response = await fetch(`${baseUrl()}/${refId}/commute?mode=${mode}`, {
    method: 'GET',
    headers: talentHeaders(token, language)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as { data: CommuteAnswer };
  return json.data;
};

/** pro-core's null for a field it cannot fill, read as the field being absent, all the way down. */
const withoutNulls = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map(withoutNulls) as T;
  if (value === null || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, field]) => field !== null)
      .map(([key, field]) => [key, withoutNulls(field)])
  ) as T;
};

const fetchJobPage = async (
  token: string,
  language: string,
  url: string
): Promise<JobPage> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: talentHeaders(token, language)
  });

  if (!response.ok) {
    await throwJobListingError(response);
  }

  const json = (await response.json()) as JobPageResponse;
  return withoutNulls(json.data);
};

/**
 * GET /talent/applications/{refId}/job: the job an application was made for, from its listing, else its opening, else
 * its assignment (`source`), never open to apply. 404 while PRO_JOB_LISTING or PRO_APPLY_V2 is off for the talent.
 */
export const fetchApplicationJob = async (
  token: string,
  language: string,
  applicationRefId: string
): Promise<JobPage> =>
  fetchJobPage(
    token,
    language,
    `${env.VITE_API_BASE_URL}/${APPLY_API_URL}/${applicationRefId}/job`
  );

/**
 * GET /talent/assignments/{assignmentRefId}/job: the job behind my job, in the same shape. The ref is the one the
 * my-job page reads the assignment with (/account/assignments/{assignmentRefId}). 404 while either flag is off.
 */
export const fetchAssignmentJob = async (
  token: string,
  language: string,
  assignmentRefId: string
): Promise<JobPage> =>
  fetchJobPage(
    token,
    language,
    `${env.VITE_API_BASE_URL}/${ASSIGNMENT_JOB_API_URL}/${assignmentRefId}/job`
  );
